import { axiosInstance } from ".";

export type RunNode =
  | "tamrur"
  | "arabic"
  | "dyncol1"
  | "dyncol2"
  | "dyncol3"
  | "news11"
  | "iplan"
  | "nwl"
  | "antique"
  | "antique_trends"
  | "boi"
  | "cbs";

export interface NodeOverviewRow {
  node: string;
  label: string;
  count_last_7d: number;
  count_prev_7d: number;
  count_3mo: number;
  total: number;
  last_collection_at: string | null;
  drop_pct: number | null;
  status: "ok" | "dropping" | "silent";
  daily_series: { day: string; count: number }[];
}

export interface SourceStatsRow {
  source_link: string | null;
  last_collection_at: string | null;
  count_last_week: number;
  count_prev_week: number;
  count_3mo: number;
  drop_pct: number | null;
  status: "ok" | "dropping" | "silent";
}

export interface RejectionReasonRow {
  source_link: string | null;
  reason: string;
  reason_label: string;
  stage: string;
  disposition: "normal" | "source" | "ours";
  count: number;
  last_seen_at: string | null;
}

export interface RejectionTotals {
  all: number;
  normal: number;
  source: number;
  ours: number;
}

export interface RejectionStatsResponse {
  node: string;
  window_days: number;
  totals: RejectionTotals;
  rows: RejectionReasonRow[];
}

export interface RejectionCandidateRow {
  link: string;
  title: string | null;
  reason: string;
  reason_label: string;
  disposition: "normal" | "source" | "ours";
  detail: string | null;
  article_date: string | null;
  last_seen_at: string | null;
  seen_count: number;
}

export interface RejectionDetailResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: RejectionCandidateRow[];
}

export interface RunSummaryRow {
  collector_name: string;
  last_started_at: string | null;
  last_finished_at: string | null;
  last_success: boolean | null;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_error_message: string | null;
  runs: number;
  failures: number;
  consecutive_failures: number;
  consecutive_empty_runs: number | null;
  stale_unfinished: boolean;
}

export interface RunStatsResponse {
  node: string;
  window_hours: number;
  rows: RunSummaryRow[];
}

export interface RunDetailRow {
  collector_name: string;
  started_at: string | null;
  finished_at: string | null;
  success: boolean | null;
  error_message: string | null;
  duration_seconds: number | null;
  items_seen: number | null;
  items_saved: number | null;
  items_rejected: number | null;
}

export interface RunDetailResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: RunDetailRow[];
}

export interface TimeSeriesPoint {
  date: string;
  count: number;
}

export interface SourceTimeseriesResponse {
  source_link: string;
  series: TimeSeriesPoint[];
}

/** @deprecated use SourceTimeseriesResponse - kept for backwards compat. */
export type TamrurTimeseriesResponse = SourceTimeseriesResponse;

const fetchSources = async (
  endpoint: string,
  token: string,
  days: number,
  threshold?: number,
): Promise<SourceStatsRow[]> => {
  const res = await axiosInstance.get(endpoint, {
    headers: { Authorization: `Token ${token}` },
    params: { days, ...(threshold !== undefined ? { threshold } : {}) },
  });
  return res.data;
};

const fetchTimeseries = async (
  endpoint: string,
  token: string,
  source_link: string,
  days: number,
): Promise<SourceTimeseriesResponse> => {
  const res = await axiosInstance.get(endpoint, {
    headers: { Authorization: `Token ${token}` },
    params: { source_link, days },
  });
  return res.data;
};

export const ParsersPanel = {
  getOverview: async (
    token: string,
    node?: string,
    days = 7
  ): Promise<NodeOverviewRow[]> => {
    const res = await axiosInstance.get("parsers_panel/overview/", {
      headers: { Authorization: `Token ${token}` },
      params: { ...(node ? { node } : {}), days },
    });
    return res.data;
  },

  getTamrurSources: (token: string, days = 7, threshold?: number) =>
    fetchSources("parsers_panel/tamrur/sources/", token, days, threshold),

  getTamrurTimeseries: (token: string, source_link: string, days = 90) =>
    fetchTimeseries("parsers_panel/tamrur/timeseries/", token, source_link, days),

  getArabicSources: (token: string, days = 7, threshold?: number) =>
    fetchSources("parsers_panel/arabic/sources/", token, days, threshold),

  getArabicTimeseries: (token: string, source_link: string, days = 90) =>
    fetchTimeseries("parsers_panel/arabic/timeseries/", token, source_link, days),

  runSnapshot: async (
    token: string,
    backfill?: number,
  ): Promise<{ ok: boolean; output: string }> => {
    const res = await axiosInstance.post(
      "parsers_panel/snapshot/",
      backfill !== undefined ? { backfill } : {},
      { headers: { Authorization: `Token ${token}` } },
    );
    return res.data;
  },

  getRejectionStats: async (
    token: string,
    node: "tamrur" | "arabic",
    days = 30
  ): Promise<RejectionStatsResponse> => {
    const res = await axiosInstance.get(
      `parsers_panel/${node}/rejections/`,
      {
        headers: { Authorization: `Token ${token}` },
        params: { days },
      }
    );
    return res.data;
  },

  getRejectionDetail: async (
    token: string,
    node: "tamrur" | "arabic",
    filters?: {
      reason?: string;
      source_link?: string;
      page?: number;
      page_size?: number;
      days?: number;
    }
  ): Promise<RejectionDetailResponse> => {
    const res = await axiosInstance.get(
      `parsers_panel/${node}/rejections/detail/`,
      {
        headers: { Authorization: `Token ${token}` },
        params: { ...filters },
      }
    );
    return res.data;
  },

  getRunStats: async (
    token: string,
    node: RunNode,
    hours = 48
  ): Promise<RunStatsResponse> => {
    const res = await axiosInstance.get(
      `parsers_panel/${node}/runs/`,
      {
        headers: { Authorization: `Token ${token}` },
        params: { hours },
      }
    );
    return res.data;
  },

  getRunDetail: async (
    token: string,
    node: RunNode,
    filters?: {
      collector_name?: string;
      success?: boolean;
      hours?: number;
      page?: number;
      page_size?: number;
    }
  ): Promise<RunDetailResponse> => {
    const res = await axiosInstance.get(
      `parsers_panel/${node}/runs/detail/`,
      {
        headers: { Authorization: `Token ${token}` },
        params: { ...filters },
      }
    );
    return res.data;
  },
};
